import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button, Alert, Spin, Typography, Slider, Input, Space } from 'antd';
import { CameraOutlined, BulbOutlined, BulbFilled } from '@ant-design/icons';

const { Text } = Typography;

const FORMATOS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'];
const GTIN_RE = /^\d{8,14}$/;

/** Leitura de código de barras no PWA — sem custo, on-device.
 *  API nativa BarcodeDetector (Chrome Android) + fallback @zxing/browser
 *  (iOS Safari e demais). Fallbacks: foto (câmera ou galeria) e digitação manual.
 */
export default function BarcodeScanner({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const trackRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const fileInputRef = useRef(null);
  const timerRef = useRef(0);
  const doneRef = useRef(false);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [zoom, setZoom] = useState(null); // { min, max, step, value } | null
  const [temLanterna, setTemLanterna] = useState(false);
  const [lanternaLigada, setLanternaLigada] = useState(false);
  const [manual, setManual] = useState('');

  // Para só a leitura (BarcodeDetector/zxing), sem soltar a câmera — reaberta em
  // seguida (próximo item) reusa o mesmo MediaStream, sem pedir permissão de novo.
  const pararDeteccao = useCallback(() => {
    clearTimeout(timerRef.current);
    try { zxingControlsRef.current?.stop?.(); } catch { /* noop */ }
    zxingControlsRef.current = null;
  }, []);

  // Solta a câmera de verdade — só ao desmontar a página (sair da lista), pra não
  // ficar com o indicador de câmera ativo à toa.
  const pararTudo = useCallback(() => {
    pararDeteccao();
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    trackRef.current = null;
  }, [pararDeteccao]);

  const finalizar = useCallback((codigo) => {
    if (doneRef.current) return;
    const limpo = String(codigo || '').replace(/\D/g, '');
    if (!GTIN_RE.test(limpo)) return;
    doneRef.current = true;
    pararDeteccao();
    onDetected(limpo);
  }, [onDetected, pararDeteccao]);

  // Libera a câmera só quando o componente desmontar de vez (sair da página).
  useEffect(() => () => pararTudo(), [pararTudo]);

  // Ajusta a trilha de vídeo: foco contínuo + descobre zoom/lanterna disponíveis.
  const ajustarTrilha = useCallback(async (track) => {
    trackRef.current = track;
    const caps = track.getCapabilities?.() || {};
    try {
      if (Array.isArray(caps.focusMode) && caps.focusMode.includes('continuous')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
      }
    } catch { /* segue sem foco contínuo */ }
    if (caps.zoom && typeof caps.zoom === 'object') {
      const settings = track.getSettings?.() || {};
      setZoom({
        min: caps.zoom.min ?? 1,
        max: caps.zoom.max ?? 1,
        step: caps.zoom.step || 0.1,
        value: settings.zoom ?? caps.zoom.min ?? 1,
      });
    }
    if (caps.torch) setTemLanterna(true);
  }, []);

  const aplicarZoom = async (valor) => {
    setZoom((z) => (z ? { ...z, value: valor } : z));
    try { await trackRef.current?.applyConstraints({ advanced: [{ zoom: valor }] }); } catch { /* noop */ }
  };

  const alternarLanterna = async () => {
    const novo = !lanternaLigada;
    try {
      await trackRef.current?.applyConstraints({ advanced: [{ torch: novo }] });
      setLanternaLigada(novo);
    } catch { /* noop */ }
  };

  useEffect(() => {
    if (!open) return undefined;
    doneRef.current = false;
    setErro(null);
    setCarregando(true);
    setZoom(null);
    setTemLanterna(false);
    setLanternaLigada(false);
    setManual('');
    let cancelado = false;

    // O <video> fica dentro do Modal (destroyOnClose), que só é montado depois deste
    // effect rodar. Na 1ª abertura o await do getUserMedia dá tempo de montar; nas
    // seguintes a stream é reusada sem await e o ref ainda estaria null.
    const esperarVideo = async () => {
      for (let i = 0; i < 60 && !cancelado; i += 1) {
        if (videoRef.current) return videoRef.current;
        await new Promise((r) => requestAnimationFrame(r));
      }
      return videoRef.current;
    };

    const iniciar = async (forcarNovaStream = false) => {
      if (forcarNovaStream) {
        streamRef.current?.getTracks?.().forEach((t) => t.stop());
        streamRef.current = null;
      }
      // Reusa o MediaStream já concedido (item anterior desta mesma sessão) em vez de
      // chamar getUserMedia de novo — evita repetir o prompt de permissão a cada scan.
      let stream = streamRef.current;
      const streamViva = stream?.getVideoTracks?.().some((t) => t.readyState === 'live');
      const reusada = Boolean(streamViva);
      if (!streamViva) {
        if (!navigator.mediaDevices?.getUserMedia) {
          setErro('Este navegador não permite acesso à câmera.');
          setCarregando(false);
          return;
        }
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          });
        } catch (e) {
          if (!cancelado) {
            setErro(
              e?.name === 'NotAllowedError'
                ? 'Permissão de câmera negada. Libere o acesso nas configurações do navegador.'
                : 'Não foi possível abrir a câmera. Use a foto ou digite o código abaixo.',
            );
            setCarregando(false);
          }
          return;
        }
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
      }

      const track = stream.getVideoTracks()[0];
      if (track) ajustarTrilha(track);

      const video = await esperarVideo();
      if (cancelado) return;
      if (!video) {
        setErro('Não foi possível iniciar a leitura ao vivo. Use a foto ou digite o código abaixo.');
        setCarregando(false);
        return;
      }
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      try { await video.play(); } catch { /* iOS às vezes exige gesto; segue */ }
      setCarregando(false);

      // 1) API nativa (Android)
      if ('BarcodeDetector' in window) {
        try {
          const detector = new window.BarcodeDetector({ formats: FORMATOS });
          const tick = async () => {
            if (doneRef.current || cancelado) return;
            try {
              const codes = await detector.detect(video);
              if (codes && codes[0]?.rawValue) {
                finalizar(codes[0].rawValue);
                return;
              }
            } catch { /* frame ruim, tenta o próximo */ }
            timerRef.current = setTimeout(tick, 250);
          };
          timerRef.current = setTimeout(tick, 400);
          return;
        } catch { /* cai no zxing */ }
      }

      // 2) Fallback @zxing/browser (iOS + demais)
      try {
        const [{ BrowserMultiFormatReader }, lib] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);
        const hints = new Map();
        hints.set(lib.DecodeHintType.POSSIBLE_FORMATS, [
          lib.BarcodeFormat.EAN_13,
          lib.BarcodeFormat.EAN_8,
          lib.BarcodeFormat.UPC_A,
          lib.BarcodeFormat.UPC_E,
          lib.BarcodeFormat.CODE_128,
        ]);
        hints.set(lib.DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints);
        zxingControlsRef.current = await reader.decodeFromVideoElement(
          video,
          (result) => {
            if (result) finalizar(result.getText());
          },
        );
      } catch {
        if (cancelado) return;
        // A stream reaproveitada pode não voltar a tocar num <video> novo (iOS);
        // tenta uma vez com uma stream nova antes de desistir.
        if (reusada) { iniciar(true); return; }
        setErro('Não foi possível iniciar a leitura ao vivo. Use a foto ou digite o código abaixo.');
      }
    };

    iniciar();
    return () => { cancelado = true; pararDeteccao(); };
  }, [open, finalizar, pararDeteccao, ajustarTrilha]);

  const lerDaFoto = async (file) => {
    if (!file) return;
    setErro(null);
    setCarregando(true);
    try {
      // 1) BarcodeDetector na imagem (rápido, quando existe)
      if ('BarcodeDetector' in window) {
        try {
          const bitmap = await createImageBitmap(file);
          const detector = new window.BarcodeDetector({ formats: FORMATOS });
          const codes = await detector.detect(bitmap);
          bitmap.close?.();
          if (codes && codes[0]?.rawValue) { finalizar(codes[0].rawValue); return; }
        } catch { /* tenta zxing */ }
      }
      // 2) zxing com TRY_HARDER
      const [{ BrowserMultiFormatReader }, lib] = await Promise.all([
        import('@zxing/browser'),
        import('@zxing/library'),
      ]);
      const hints = new Map();
      hints.set(lib.DecodeHintType.POSSIBLE_FORMATS, [
        lib.BarcodeFormat.EAN_13,
        lib.BarcodeFormat.EAN_8,
        lib.BarcodeFormat.UPC_A,
        lib.BarcodeFormat.UPC_E,
        lib.BarcodeFormat.CODE_128,
      ]);
      hints.set(lib.DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints);
      const url = URL.createObjectURL(file);
      try {
        const result = await reader.decodeFromImageUrl(url);
        finalizar(result.getText());
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      setErro('Não consegui ler o código nessa foto. Enquadre o código preenchendo a largura da tela, com boa luz — ou digite o número abaixo.');
    } finally {
      setCarregando(false);
    }
  };

  const enviarManual = () => {
    const limpo = manual.replace(/\D/g, '');
    if (!GTIN_RE.test(limpo)) {
      setErro('O código de barras tem entre 8 e 14 dígitos.');
      return;
    }
    finalizar(limpo);
  };

  return (
    <Modal
      title="Escanear código de barras"
      open={open}
      onCancel={onClose}
      footer={[<Button key="c" onClick={onClose}>Cancelar</Button>]}
      destroyOnClose
    >
      {erro && <Alert type="warning" showIcon message={erro} style={{ marginBottom: 12 }} />}
      <div style={{ position: 'relative', background: '#000', borderRadius: 8, overflow: 'hidden', aspectRatio: '4 / 3' }}>
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{
          position: 'absolute', inset: '30% 6%', border: '2px solid #50B0A0',
          borderRadius: 8, pointerEvents: 'none',
        }} />
        {carregando && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin />
          </div>
        )}
        {temLanterna && (
          <Button
            shape="circle"
            icon={lanternaLigada ? <BulbFilled /> : <BulbOutlined />}
            onClick={alternarLanterna}
            style={{ position: 'absolute', top: 8, right: 8 }}
          />
        )}
      </div>

      {zoom && zoom.max > zoom.min && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Zoom</Text>
          <Slider
            min={zoom.min}
            max={zoom.max}
            step={zoom.step}
            value={zoom.value}
            onChange={aplicarZoom}
            tooltip={{ open: false }}
          />
        </div>
      )}

      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Preencha a largura da tela com o código, a ~15 cm, com boa luz. Se não pegar em alguns
        segundos, use a foto ou digite o número.
      </Text>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => lerDaFoto(e.target.files?.[0])}
      />
      <Button
        icon={<CameraOutlined />}
        onClick={() => fileInputRef.current?.click()}
        block
        style={{ marginTop: 12 }}
      >
        Não leu? Usar uma foto do código
      </Button>

      <Space.Compact style={{ width: '100%', marginTop: 12 }}>
        <Input
          placeholder="Ou digite o código de barras"
          inputMode="numeric"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onPressEnter={enviarManual}
          maxLength={14}
        />
        <Button type="primary" onClick={enviarManual}>OK</Button>
      </Space.Compact>
    </Modal>
  );
}
